import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Users, Search, Eye, EyeOff, Link2, KeyRound } from 'lucide-react';

const Usuarios = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getCondominioId, condominios, condominioSelecionado } = useCondominio();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [senhaConfirmacao, setSenhaConfirmacao] = useState('');
  const [filters, setFilters] = useState({
    tipo_acesso: '',
    status: '',
    nome: '',
    email: '',
    cpf: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [formData, setFormData] = useState({
    usuario_nome: '',
    usuario_email: '',
    usuario_cpf: '',
    usuario_senha: '',
    usuario_telefone: '',
    usuario_tipo_acesso: 'morador',
    usuario_status: 'ativo',
    condominio_id: ''
  });

  useEffect(() => {
    loadUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage]);
  
  // Recarregar quando filtros ou condomínio mudarem
  useEffect(() => {
    if (pagination.currentPage === 1) {
      loadUsuarios();
    } else {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.tipo_acesso, filters.status, filters.nome, filters.email, filters.cpf, condominioSelecionado?.condominio_id]);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      
      // Paginação
      params.append('page', pagination.currentPage);
      params.append('limit', pagination.itemsPerPage);
      
      // Administrador: listar todos os usuários de todos os condomínios (não filtrar por condominio_id)
      // Outros perfis: filtrar pelo condomínio selecionado no header
      const isAdmin = user?.tipo === 'administrador';
      if (!isAdmin) {
        const condominioId = getCondominioId();
        if (condominioId) params.append('condominio_id', condominioId);
      }
      if (filters.tipo_acesso) params.append('tipo_acesso', filters.tipo_acesso);
      if (filters.status) params.append('status', filters.status);
      const cpfDigits = (filters.cpf || '').replace(/\D/g, '');
      if (cpfDigits) {
        // CPF: usa busca geral do backend (inclui CPF) para simplificar
        params.append('search', cpfDigits);
      } else {
        if (filters.nome) params.append('nome', filters.nome);
        if (filters.email) params.append('email', filters.email);
      }

      const response = await api.get(`/usuarios?${params}`);

      // Garantir que os dados estão no formato esperado
      const usuariosData = response.data.data?.usuarios || response.data.data || [];
      // Filtrar usuários inválidos e garantir estrutura correta
      const usuariosValidos = usuariosData.filter(u => {
        if (!u) return false;
        // Verificar se tem dados do usuário (pode estar em u.usuario ou diretamente em u)
        const usuarioData = u.usuario || u;
        return usuarioData && (usuarioData.usuario_id || usuarioData.usuario_nome || u.usuario_id || u.usuario_nome);
      });
      
      console.log('📊 Usuários processados:', {
        totalRecebido: usuariosData.length,
        totalValidos: usuariosValidos.length
      });
      
      setUsuarios(usuariosValidos);
      
      // Atualizar paginação
      if (response.data.data?.pagination) {
        setPagination(prev => ({
          ...prev,
          ...response.data.data.pagination
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      if (!editingUsuario) {
        if (!formData.usuario_senha || formData.usuario_senha.length < 6) {
          setErrorMessage('A senha deve ter pelo menos 6 caracteres');
          setIsSubmitting(false);
          return;
        }
        if (formData.usuario_senha !== senhaConfirmacao) {
          setErrorMessage('A senha e a confirmação não correspondem');
          setIsSubmitting(false);
          return;
        }
      }

      const condominioId = formData.condominio_id || getCondominioId();
      if (!condominioId) {
        setErrorMessage('Selecione um condomínio para criar o usuário');
        setIsSubmitting(false);
        return;
      }
      const dadosParaEnvio = {
        ...formData,
        condominio_id: condominioId
      };

      // Se está editando, não enviar senha se estiver vazia
      if (editingUsuario && !formData.usuario_senha) {
        delete dadosParaEnvio.usuario_senha;
      }

      let result;
      if (editingUsuario) {
        result = await api.put(`/usuarios/${editingUsuario.usuario_condominio_id}`, dadosParaEnvio);
      } else {
        result = await api.post('/usuarios', dadosParaEnvio);
      }

      if (result.data.success) {
        setShowModal(false);
        resetForm();
        loadUsuarios();
      } else {
        setErrorMessage(result.data.message || 'Erro ao salvar usuário');
      }
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Se houver múltiplos erros de validação
        if (Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map(err => 
            err.msg || err.message || err
          ).join(', ');
          setErrorMessage(errorMessages);
        } else {
          setErrorMessage('Erro de validação');
        }
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro ao salvar usuário. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (usuario) => {
    if (!usuario) {
      console.error('Usuário não fornecido para edição');
      return;
    }
    
    setEditingUsuario(usuario);
    setSenhaConfirmacao('');
    setMostrarSenha(false);
    const usuarioData = usuario.usuario || usuario; // Suportar ambos os formatos
    
    if (!usuarioData) {
      console.error('Dados do usuário não encontrados:', usuario);
      return;
    }
    
    setFormData({
      usuario_nome: usuarioData.usuario_nome || usuarioData.nome || usuario.nome || '',
      usuario_email: usuarioData.usuario_email || usuarioData.email || usuario.email || '',
      usuario_cpf: usuarioData.usuario_cpf || usuarioData.cpf || usuario.cpf || '',
      usuario_senha: '',
      usuario_telefone: usuarioData.usuario_telefone || usuarioData.telefone || usuario.telefone || '',
      usuario_tipo_acesso: usuario.tipo_acesso || usuario.usuario_tipo_acesso || usuarioData.usuario_tipo || usuarioData.tipo || 'morador',
      usuario_status: usuario.status || usuario.usuario_status || (usuarioData.usuario_ativo === false ? 'inativo' : 'ativo'),
      condominio_id: usuario.condominio_id || usuario.condominio?.condominio_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (usuario) => {
    const usuarioId = usuario?.usuario_id || usuario?.usuario?.usuario_id;
    const vinculoId = usuario?.usuario_condominio_id;
    const nomeCondo = usuario?.condominio?.condominio_nome || 'este condomínio';

    if (vinculoId) {
      if (!window.confirm(`Remover o vínculo com ${nomeCondo}? Os outros condomínios deste usuário permanecem.`)) return;
      try {
        const result = await api.delete(`/usuarios/vinculos/${vinculoId}`);
        if (result.data.success) loadUsuarios();
        else alert(result.data?.message || 'Erro ao remover o vínculo');
      } catch (error) {
        alert(error.response?.data?.message || 'Erro ao remover o vínculo');
      }
      return;
    }

    if (!usuarioId) return;
    if (!window.confirm('Desativar este usuário? Ele não está vinculado a um condomínio.')) return;

    try {
      const result = await api.delete(`/usuarios/${usuarioId}`);

      if (result.data.success) {
        loadUsuarios();
      } else {
        alert(result.data?.message || 'Erro ao desativar usuário');
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao desativar usuário.');
    }
  };

  const resetForm = () => {
    setFormData({
      usuario_nome: '',
      usuario_email: '',
      usuario_cpf: '',
      usuario_senha: '',
      usuario_telefone: '',
      usuario_tipo_acesso: 'morador',
      usuario_status: 'ativo',
      condominio_id: condominioSelecionado?.condominio_id || ''
    });
    setEditingUsuario(null);
    setErrorMessage('');
    setMostrarSenha(false);
    setSenhaConfirmacao('');
  };

  const applyPhoneMask = (value) => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'usuario_telefone') {
      const maskedValue = applyPhoneMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: maskedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Resetar para primeira página ao filtrar
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const applyFilters = () => {
    loadUsuarios();
  };

  const clearFilters = () => {
    setFilters({
      tipo_acesso: '',
      status: '',
      nome: '',
      email: '',
      cpf: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'ativo': 'bg-green-100 text-green-800',
      'inativo': 'bg-gray-100 text-gray-800',
      'bloqueado': 'bg-red-100 text-red-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getTipoAcessoBadge = (tipo) => {
    const tipoMap = {
      'administrador': 'bg-purple-100 text-purple-800',
      'sindico': 'bg-blue-100 text-blue-800',
      'subsindico': 'bg-cyan-100 text-cyan-800',
      'porteiro': 'bg-green-100 text-green-800',
      'morador': 'bg-gray-100 text-gray-800',
      'funcionario': 'bg-orange-100 text-orange-800'
    };
    return tipoMap[tipo] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
        </div>
        <button
          onClick={() => {
          resetForm();
        setFormData(prev => ({
          ...prev,
          condominio_id: condominioSelecionado?.condominio_id || ''
        }));
        setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Novo Usuário
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Acesso</label>
            <select
              name="tipo_acesso"
              value={filters.tipo_acesso}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Todos</option>
              <option value="administrador">Administrador</option>
              <option value="sindico">Síndico</option>
              <option value="subsindico">Subsíndico</option>
              <option value="porteiro">Porteiro</option>
              <option value="morador">Morador</option>
              <option value="funcionario">Funcionário</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Todos</option>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="bloqueado">Bloqueado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input
              type="text"
              name="nome"
              value={filters.nome}
              onChange={handleFilterChange}
              placeholder="Buscar por nome"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="text"
              name="email"
              value={filters.email}
              onChange={handleFilterChange}
              placeholder="Buscar por email"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
            <input
              type="text"
              name="cpf"
              value={filters.cpf}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (value.length > 11) value = value.slice(0, 11);
                value = value
                  .replace(/(\d{3})(\d)/, '$1.$2')
                  .replace(/(\d{3})(\d)/, '$1.$2')
                  .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                setFilters(prev => ({ ...prev, cpf: value }));
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
              placeholder="000.000.000-00"
              maxLength={14}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Search className="h-4 w-4" />
              Filtrar
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Condomínio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo de Acesso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apartamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Vínculo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    Carregando...
                  </td>
                </tr>
              ) : usuarios.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    Nenhum usuário encontrado
                  </td>
                </tr>
              ) : (
                usuarios.map((usuario, index) => {
                  // Suportar ambos os formatos: usuario.usuario ou usuario direto
                  if (!usuario) {
                    console.warn('Usuário nulo no índice:', index);
                    return null;
                  }
                  
                  const usuarioData = usuario.usuario || usuario;
                  if (!usuarioData) {
                    console.warn('Usuário sem dados no índice:', index, usuario);
                    return null; // Pular se não tiver dados
                  }
                  
                  // Garantir que temos um ID válido para a key
                  const usuarioKey = usuario.usuario_condominio_id 
                    || usuario.usuario_id 
                    || (usuarioData && usuarioData.usuario_id) 
                    || `usuario-${index}`;
                  
                  return (
                  <tr key={usuarioKey} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {usuarioData.usuario_nome || 'N/A'}
                      </div>
                      {usuarioData.usuario_telefone && (
                        <div className="text-sm text-gray-500">
                          {usuarioData.usuario_telefone}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usuarioData.usuario_email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usuarioData.usuario_cpf || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usuario.condominio?.condominio_nome || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoAcessoBadge(usuario.tipo_acesso || usuario.usuario_tipo_acesso || usuarioData.usuario_tipo || 'morador')}`}>
                        {usuario.tipo_acesso || usuario.usuario_tipo_acesso || usuarioData.usuario_tipo || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {usuario.apartamento ? 
                        `${usuario.apartamento.apartamento_bloco || ''} - ${usuario.apartamento.apartamento_numero || ''}`.trim() || 'N/A' : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(usuario.status || usuario.usuario_status || 'ativo')}`}>
                        {usuario.status || usuario.usuario_status || 'ativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(usuario.dt_criacao || usuario.usuario_dt_vinculo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(usuario)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/usuarios/${usuarioData.usuario_id || usuario.usuario_id}/vinculos`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Vincular / desvincular condomínio"
                        >
                          <Link2 className="h-4 w-4" />
                        </button>
                        {(user?.tipo === 'administrador' || user?.usuario_tipo === 'administrador') && (
                          <button
                            onClick={() => navigate(`/usuarios/${usuarioData.usuario_id || usuario.usuario_id}/senha`, { state: { nome: usuarioData.usuario_nome || usuario.usuario_nome || '' } })}
                            className="text-amber-600 hover:text-amber-800"
                            title="Alterar senha"
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(usuario)}
                          className="text-red-600 hover:text-red-900"
                          title={usuario.usuario_condominio_id ? 'Remover vínculo deste condomínio' : 'Desativar usuário'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> a{' '}
            <span className="font-medium">
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
            </span>{' '}
            de <span className="font-medium">{pagination.totalItems}</span> usuários
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 border rounded-md text-sm font-medium ${
                      pagination.currentPage === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
            
            {errorMessage && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!editingUsuario && user?.tipo === 'administrador' && condominios?.length > 1 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Condomínio *
                    </label>
                    <select
                      name="condominio_id"
                      value={formData.condominio_id || condominioSelecionado?.condominio_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, condominio_id: e.target.value ? parseInt(e.target.value) : '' }))}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Selecione o condomínio</option>
                      {condominios.map(c => (
                        <option key={c.condominio_id} value={c.condominio_id}>{c.condominio_nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="usuario_nome"
                    value={formData.usuario_nome}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="usuario_email"
                    value={formData.usuario_email}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="exemplo@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF
                  </label>
                  <input
                    type="text"
                    name="usuario_cpf"
                    value={formData.usuario_cpf}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                        setFormData(prev => ({ ...prev, usuario_cpf: value }));
                      }
                    }}
                    placeholder="000.000.000-00"
                    maxLength="14"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha {!editingUsuario && '*'}
                  </label>
                  <div className="relative">
                    <input
                      type={!editingUsuario && mostrarSenha ? 'text' : 'password'}
                      name="usuario_senha"
                      value={formData.usuario_senha}
                      onChange={handleInputChange}
                      required={!editingUsuario}
                      minLength="6"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10"
                      placeholder={editingUsuario ? "Deixe vazio para manter a senha atual" : ""}
                    />
                    {!editingUsuario && (
                      <button
                        type="button"
                        onClick={() => setMostrarSenha((atual) => !atual)}
                        className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
                        title={mostrarSenha ? 'Ocultar senha' : 'Exibir senha'}
                      >
                        {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>
                {!editingUsuario && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmar senha *
                    </label>
                    <input
                      type={mostrarSenha ? 'text' : 'password'}
                      value={senhaConfirmacao}
                      onChange={(e) => setSenhaConfirmacao(e.target.value)}
                      required
                      minLength="6"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    {senhaConfirmacao && formData.usuario_senha !== senhaConfirmacao && (
                      <p className="mt-1 text-sm text-red-600">A senha e a confirmação não correspondem</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    name="usuario_telefone"
                    value={formData.usuario_telefone}
                    onChange={handleInputChange}
                    maxLength="15"
                    placeholder="(11) 99999-9999"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Acesso *
                  </label>
                  <select
                    name="usuario_tipo_acesso"
                    value={formData.usuario_tipo_acesso}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="morador">Morador</option>
                    <option value="funcionario">Funcionário</option>
                    <option value="porteiro">Porteiro</option>
                    <option value="subsindico">Subsíndico</option>
                    <option value="sindico">Síndico</option>
                    <option value="administrador">Administrador</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.usuario_status === 'ativo'}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        usuario_status: e.target.checked ? 'ativo' : 'inativo'
                      }))}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    Ativo
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
